/**
 * Google PageSpeed / Lighthouse Adapter (Story 4.11, IMC-10)
 *
 * FIRST non-OAuth adapter: uses server-side API key, NOT OAuth tokens.
 * `authType: 'api-key'` — OAuth credentials are accepted but ignored.
 *
 * Fetches Lighthouse lab metrics + all 5 category scores for both
 * Mobile and Desktop strategies in parallel via Promise.all().
 *
 * CrUX field data extracted with full distributions (URL + origin level).
 * CrUX History API provides 40-week trend data.
 * Lighthouse 13 insight audits parsed for actionable recommendations.
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-014: Defensive API Versioning (versions via API_VERSIONS)
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { API_VERSIONS } from "../config";
import { fetchCruxHistory } from "./crux-history";
import { fetchCruxRecord, CRUX_FORM_FACTORS } from "./crux-record";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "google-pagespeed" as const;

/** PSI API endpoint — version from API_VERSIONS, NO hardcoded strings */
const PSI_BASE = `https://www.googleapis.com/pagespeedonline/${API_VERSIONS.pagespeed}/runPagespeed`;

/** Per-call timeout: 12s. All network calls run concurrently via Promise.all:
 *  - 2 PSI (MOBILE + DESKTOP, 12s each)
 *  - 1 CrUX History (10s — see crux-history.ts)
 *  - 3 CrUX Record (1 per form factor, 8s each — see crux-record.ts)
 *  Wall clock bounded by the slowest call (<=12s), well within Workers' 30s CPU limit. */
const QUERY_TIMEOUT_MS = 12_000;

/** Lighthouse audit keys to extract */
const LIGHTHOUSE_AUDITS = [
  "first-contentful-paint",
  "largest-contentful-paint",
  "total-blocking-time",
  "cumulative-layout-shift",
  "speed-index",
] as const;

/** Mapping from audit key to our metric key */
const AUDIT_TO_METRIC: Record<string, string> = {
  "first-contentful-paint": "fcp",
  "largest-contentful-paint": "lcp",
  "total-blocking-time": "tbt",
  "cumulative-layout-shift": "cls",
  "speed-index": "si",
};

/** Mapping from audit key to rating field name */
const AUDIT_TO_RATING: Record<string, string> = {
  "first-contentful-paint": "fcpRating",
  "largest-contentful-paint": "lcpRating",
  "total-blocking-time": "tbtRating",
  "cumulative-layout-shift": "clsRating",
  "speed-index": "siRating",
};

/** Lighthouse 13 insight audit IDs (replaces legacy audit IDs) */
const INSIGHT_AUDIT_IDS = [
  "lcp-discovery-insight",
  "lcp-phases-insight",
  "cls-culprits-insight",
  "interaction-to-next-paint-insight",
  "document-latency-insight",
  "image-delivery-insight",
  "third-parties-insight",
  "dom-size-insight",
  "render-blocking-insight",
  "legacy-javascript-insight",
] as const;

/** Mapping from insight audit ID to metric key */
const INSIGHT_TO_METRIC: Record<string, string> = {
  "lcp-discovery-insight": "insight_lcp_discovery",
  "lcp-phases-insight": "insight_lcp_phases",
  "cls-culprits-insight": "insight_cls_culprits",
  "interaction-to-next-paint-insight": "insight_inp",
  "document-latency-insight": "insight_document_latency",
  "image-delivery-insight": "insight_image_delivery",
  "third-parties-insight": "insight_third_parties",
  "dom-size-insight": "insight_dom_size",
  "render-blocking-insight": "insight_render_blocking",
  "legacy-javascript-insight": "insight_legacy_javascript",
};

/** CrUX metric keys as returned by PSI API */
const CRUX_METRIC_KEYS = [
  "FIRST_CONTENTFUL_PAINT_MS",
  "LARGEST_CONTENTFUL_PAINT_MS",
  "CUMULATIVE_LAYOUT_SHIFT_SCORE",
  "INTERACTION_TO_NEXT_PAINT",
  "EXPERIMENTAL_TIME_TO_FIRST_BYTE",
  // "FIRST_INPUT_DELAY_MS" — removed: FID deprecated by Chrome, replaced by INP
] as const;

/** Mapping from CrUX API metric key to our metric key prefix */
const CRUX_KEY_TO_PREFIX: Record<string, string> = {
  "FIRST_CONTENTFUL_PAINT_MS": "crux_fcp",
  "LARGEST_CONTENTFUL_PAINT_MS": "crux_lcp",
  "CUMULATIVE_LAYOUT_SHIFT_SCORE": "crux_cls",
  "INTERACTION_TO_NEXT_PAINT": "crux_inp",
  "EXPERIMENTAL_TIME_TO_FIRST_BYTE": "crux_ttfb",
  // "FIRST_INPUT_DELAY_MS": "crux_fid", — removed: FID deprecated, replaced by INP
};

// ============================================================================
// Response Types (local to adapter, NOT exported)
// ============================================================================

interface LighthouseAudit {
  numericValue?: number;
  score?: number | null;
  displayValue?: string;
  details?: {
    type?: string;
    items?: unknown[];
  };
}

interface CategoryScore {
  score?: number | null;
}

interface CrUXDistribution {
  min?: number;
  max?: number;
  proportion?: number;
}

interface CrUXMetric {
  percentile?: number;
  category?: string;
  distributions?: CrUXDistribution[];
}

interface LoadingExperience {
  metrics?: Record<string, CrUXMetric>;
  overall_category?: string;
}

interface PSIResponse {
  lighthouseResult?: {
    categories?: {
      performance?: CategoryScore;
      accessibility?: CategoryScore;
      "best-practices"?: CategoryScore;
      seo?: CategoryScore;
      pwa?: CategoryScore;
    };
    audits?: Record<string, LighthouseAudit>;
  };
  loadingExperience?: LoadingExperience;
  originLoadingExperience?: LoadingExperience;
  analysisUTCTimestamp?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Classify a Lighthouse score (0-100) into a rating.
 */
function classifyScore(score: number): "good" | "needs-improvement" | "poor" {
  if (score >= 90) return "good";
  if (score >= 50) return "needs-improvement";
  return "poor";
}

/**
 * Classify an individual audit score (0-1 float) into a rating.
 */
function classifyAuditScore(score: number | null | undefined): "good" | "needs-improvement" | "poor" {
  if (score == null) return "poor";
  if (score >= 0.9) return "good";
  if (score >= 0.5) return "needs-improvement";
  return "poor";
}

/**
 * Convert a 0-1 Lighthouse category score to 0-100 integer.
 * Returns null if the score is missing.
 */
function normalizeCategoryScore(score: number | null | undefined): number | null {
  return score != null ? Math.round(score * 100) : null;
}

/**
 * Extract Lighthouse lab metrics, category scores, diagnostics, and insight audits from PSI response.
 */
function extractLighthouseMetrics(
  response: PSIResponse,
  strategy: string
): Record<string, unknown> {
  const lighthouse = response.lighthouseResult;
  const categories = lighthouse?.categories;

  const performanceScore = normalizeCategoryScore(categories?.performance?.score);
  const accessibilityScore = normalizeCategoryScore(categories?.accessibility?.score);
  const bestPracticesScore = normalizeCategoryScore(categories?.["best-practices"]?.score);
  const seoScore = normalizeCategoryScore(categories?.seo?.score);
  const pwaScore = normalizeCategoryScore(categories?.pwa?.score);

  const record: Record<string, unknown> = {
    _reportType: "lighthouse",
    strategy: strategy.toLowerCase(),
    performanceScore,
    performanceRating: performanceScore != null ? classifyScore(performanceScore) : "poor",
    accessibilityScore,
    bestPracticesScore,
    seoScore,
    pwaScore,
  };

  // Extract each audit metric defensively
  for (const auditKey of LIGHTHOUSE_AUDITS) {
    const audit = lighthouse?.audits?.[auditKey];
    const metricKey = AUDIT_TO_METRIC[auditKey];
    const ratingKey = AUDIT_TO_RATING[auditKey];

    if (metricKey) {
      record[metricKey] = audit?.numericValue ?? null;
    }
    if (ratingKey) {
      record[ratingKey] = classifyAuditScore(audit?.score);
    }
  }

  // Extract diagnostic metrics: lab TTFB and TTI (Task 4)
  record.lab_ttfb = lighthouse?.audits?.["server-response-time"]?.numericValue ?? null;
  record.lab_tti = lighthouse?.audits?.["interactive"]?.numericValue ?? null;

  // Extract Lighthouse 13 insight audits (Task 3)
  for (const insightId of INSIGHT_AUDIT_IDS) {
    const audit = lighthouse?.audits?.[insightId];
    const metricKey = INSIGHT_TO_METRIC[insightId];
    if (metricKey) {
      record[metricKey] = audit?.score != null ? audit.score : null;
      record[`${metricKey}_display`] = audit?.displayValue ?? null;
      record[`${metricKey}_details`] = audit?.details?.items ?? null;
    }
  }

  return record;
}

/**
 * Extract CrUX field data from a LoadingExperience object.
 * Returns null if no CrUX data is present.
 */
function extractCruxFromLoadingExperience(
  loadingExp: LoadingExperience | undefined,
  reportType: "cruxUrl" | "cruxOrigin",
  strategy: string
): Record<string, unknown> | null {
  const metrics = loadingExp?.metrics;

  if (!metrics || Object.keys(metrics).length === 0) {
    return null;
  }

  const record: Record<string, unknown> = {
    _reportType: reportType,
    strategy: strategy.toLowerCase(),
    crux_overall_category: loadingExp?.overall_category ?? null,
  };

  // Extract each CrUX metric with p75, category, and distribution percentages
  for (const apiKey of CRUX_METRIC_KEYS) {
    const prefix = CRUX_KEY_TO_PREFIX[apiKey];
    const metric = metrics[apiKey];

    if (!prefix) continue;

    record[prefix] = metric?.percentile ?? null;
    record[`${prefix}_category`] = metric?.category ?? null;

    // Extract distribution proportions (good / needs-improvement / poor)
    const distributions = metric?.distributions;
    if (distributions && distributions.length >= 3) {
      record[`${prefix}_good_pct`] = distributions[0]?.proportion ?? null;
      record[`${prefix}_needs_improvement_pct`] = distributions[1]?.proportion ?? null;
      record[`${prefix}_poor_pct`] = distributions[2]?.proportion ?? null;
    } else {
      record[`${prefix}_good_pct`] = null;
      record[`${prefix}_needs_improvement_pct`] = null;
      record[`${prefix}_poor_pct`] = null;
    }
  }

  return record;
}

/**
 * Extract CrUX records from PSI response.
 * Returns both URL-specific and origin-level CrUX records.
 * If URL-level has NONE overall_category, falls back to origin data.
 */
function extractCruxRecords(
  response: PSIResponse,
  strategy: string
): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = [];

  const urlCrux = extractCruxFromLoadingExperience(
    response.loadingExperience, "cruxUrl", strategy
  );
  const originCrux = extractCruxFromLoadingExperience(
    response.originLoadingExperience, "cruxOrigin", strategy
  );

  // If URL-level has NONE category (insufficient data), use origin as fallback
  if (urlCrux && urlCrux.crux_overall_category === "NONE" && originCrux) {
    // Replace URL record with origin data but keep cruxUrl reportType for compatibility
    const fallback = { ...originCrux, _reportType: "cruxUrl", _fallbackFromOrigin: true };
    records.push(fallback);
  } else if (urlCrux) {
    records.push(urlCrux);
  }

  if (originCrux) {
    records.push(originCrux);
  }

  return records;
}

/**
 * Fetch PSI data for a single strategy.
 * Constructs URL using URL/URLSearchParams (NEVER string concatenation).
 */
async function fetchStrategy(
  targetUrl: string,
  strategy: string,
  apiKey: string
): Promise<AdapterResult<PSIResponse>> {
  const apiUrl = new URL(PSI_BASE);
  apiUrl.searchParams.set("url", targetUrl);
  apiUrl.searchParams.set("key", apiKey);
  apiUrl.searchParams.set("strategy", strategy);
  // Request 4 Lighthouse categories (PWA category deprecated by Lighthouse 12+)
  for (const cat of ["performance", "accessibility", "best-practices", "seo"]) {
    apiUrl.searchParams.append("category", cat);
  }

  const result = await safeFetch(apiUrl.toString(), {}, QUERY_TIMEOUT_MS);

  if (!result.success) {
    return adapterErr({
      ...result.error,
      message: sanitizeErrorMessage(result.error.message),
    });
  }

  try {
    const data = (await result.data.json()) as PSIResponse;
    return adapterOk(data);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse PageSpeed response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// Available Metrics Definition
// ============================================================================

const PAGESPEED_METRICS: MetricDefinition[] = [
  // Lighthouse category scores (0-100)
  { key: "performanceScore", label: "Performance Score", type: "number", category: "score", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "accessibilityScore", label: "Accessibility Score", type: "number", category: "score", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "bestPracticesScore", label: "Best Practices Score", type: "number", category: "score", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "seoScore", label: "SEO Score", type: "number", category: "score", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "pwaScore", label: "PWA Score (deprecated)", type: "number", category: "score", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"], deprecated: true }, // Lighthouse 12+ removed PWA category

  // Lighthouse lab metrics
  { key: "fcp", label: "First Contentful Paint", type: "duration", category: "performance", dataType: "Duration", aggregationKind: "ratio" },
  { key: "lcp", label: "Largest Contentful Paint", type: "duration", category: "performance", dataType: "Duration", aggregationKind: "ratio" },
  { key: "tbt", label: "Total Blocking Time", type: "duration", category: "performance", dataType: "Duration", aggregationKind: "ratio" },
  { key: "cls", label: "Cumulative Layout Shift", type: "number", category: "performance", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "si", label: "Speed Index", type: "duration", category: "performance", dataType: "Duration", aggregationKind: "ratio" },

  // Diagnostic lab metrics (Task 4)
  { key: "lab_ttfb", label: "Lab TTFB (Server Response)", type: "duration", category: "diagnostic", dataType: "Duration", aggregationKind: "ratio" },
  { key: "lab_tti", label: "Lab TTI (Time to Interactive)", type: "duration", category: "diagnostic", dataType: "Duration", aggregationKind: "ratio" },

  // CrUX field metrics — p75 values
  { key: "crux_fcp", label: "CrUX FCP (p75)", type: "duration", category: "cwv", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "crux_lcp", label: "CrUX LCP (p75)", type: "duration", category: "cwv", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "crux_cls", label: "CrUX CLS (p75)", type: "number", category: "cwv", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "crux_inp", label: "CrUX INP (p75)", type: "duration", category: "cwv", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "crux_ttfb", label: "CrUX TTFB (p75)", type: "duration", category: "cwv", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "crux_fid", label: "CrUX FID (deprecated, use INP)", type: "duration", category: "cwv", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi"], deprecated: true }, // Chrome removed FID Sept 2024

  // CrUX distribution percentages (good/needs-improvement/poor for each metric)
  { key: "crux_fcp_good_pct", label: "FCP Good %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_fcp_needs_improvement_pct", label: "FCP Needs Improvement %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_fcp_poor_pct", label: "FCP Poor %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_lcp_good_pct", label: "LCP Good %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_lcp_needs_improvement_pct", label: "LCP Needs Improvement %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_lcp_poor_pct", label: "LCP Poor %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_cls_good_pct", label: "CLS Good %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_cls_needs_improvement_pct", label: "CLS Needs Improvement %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_cls_poor_pct", label: "CLS Poor %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_inp_good_pct", label: "INP Good %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_inp_needs_improvement_pct", label: "INP Needs Improvement %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_inp_poor_pct", label: "INP Poor %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_ttfb_good_pct", label: "TTFB Good %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_ttfb_needs_improvement_pct", label: "TTFB Needs Improvement %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_ttfb_poor_pct", label: "TTFB Poor %", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "crux_fid_good_pct", label: "FID Good % (deprecated, use INP)", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio", deprecated: true },
  { key: "crux_fid_needs_improvement_pct", label: "FID Needs Improvement % (deprecated, use INP)", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio", deprecated: true },
  { key: "crux_fid_poor_pct", label: "FID Poor % (deprecated, use INP)", type: "percentage", category: "cwv-distribution", dataType: "Percentage", aggregationKind: "ratio", deprecated: true },
  // crux_overall_category is a categorical string (FAST/AVERAGE/SLOW/NONE), not a numeric metric — lives in record data only

  // Lighthouse 13 insight audit scores (Task 3)
  { key: "insight_lcp_discovery", label: "LCP Discovery Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_lcp_phases", label: "LCP Phases Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_cls_culprits", label: "CLS Culprits Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_inp", label: "INP Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_document_latency", label: "Document Latency Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_image_delivery", label: "Image Delivery Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_third_parties", label: "Third Parties Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_dom_size", label: "DOM Size Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_render_blocking", label: "Render Blocking Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },
  { key: "insight_legacy_javascript", label: "Legacy JavaScript Insight", type: "number", category: "insight", dataType: "Decimal", aggregationKind: "ratio" },

  // CrUX Record-API-only metrics (IMC-27, Task 1)
  // Available ONLY from the dedicated CrUX API (chromeuxreport.googleapis.com),
  // NOT from PSI. See crux-record.ts for API details.
  //
  // NOTE on the two Enum metrics (lcp_resource_type, navigation_types):
  // These register the DOMINANT label as a categorical value. The full fraction
  // breakdowns are published as record fields `lcp_resource_type_fractions`
  // and `navigation_type_fractions` (not as individual metric definitions) —
  // same pattern as `crux_overall_category`. Widgets that need the full
  // breakdown should consume those fields directly from cruxRecord rows.
  { key: "lcp_image_ttfb", label: "LCP Image TTFB (p75)", type: "duration", category: "cwv-subparts", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "lcp_image_load_delay", label: "LCP Image Load Delay (p75)", type: "duration", category: "cwv-subparts", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "lcp_image_load_duration", label: "LCP Image Load Duration (p75)", type: "duration", category: "cwv-subparts", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "lcp_image_render_delay", label: "LCP Image Render Delay (p75)", type: "duration", category: "cwv-subparts", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "lcp_resource_type", label: "LCP Resource Type (dominant)", type: "number", category: "cwv-breakdown", dataType: "Enum", compatibleWidgets: ["kpi", "data-table"] },
  { key: "round_trip_time", label: "Network Round-Trip Time (p75)", type: "duration", category: "cwv", dataType: "Duration", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"] },
  // `navigation_type` stores the dominant label (navigate/reload/back_forward/etc.).
  // The fraction breakdown lives in the record field `navigation_type_fractions`
  // (not a separate metric). Matches the singular-label pattern of `lcp_resource_type`.
  // AC #1 document uses "navigation_types" loosely; the singular is semantically
  // correct for a single dominant label.
  { key: "navigation_type", label: "Navigation Type (dominant)", type: "number", category: "cwv-breakdown", dataType: "Enum", compatibleWidgets: ["kpi", "data-table"] },
];

// ============================================================================
// Available Dimensions Definition (Story 5.34)
// ============================================================================

const PAGESPEED_DIMENSIONS: DimensionDefinition[] = [
  { key: "strategy", label: "Strategy", type: "Tech", exampleValues: ["mobile", "desktop"] },
  // form_factor (PHONE/DESKTOP/TABLET) comes from CrUX queryRecord calls (IMC-27, Task 2)
  { key: "form_factor", label: "Form Factor", type: "Tech", exampleValues: ["phone", "desktop", "tablet"] },
];

// ============================================================================
// PageSpeed Adapter (IntegrationAdapter implementation)
// ============================================================================

export const pagespeedAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  authType: "api-key",

  rateLimits: {
    requestsPerMinute: 240,
    requestsPerDay: 25_000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    _credentials: OAuthCredentials,
    _dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const targetUrl = ctx.accountId;

    if (!targetUrl) {
      return adapterErr({
        code: "account_not_found",
        message: "No URL configured for PageSpeed analysis.",
        retryable: false,
      });
    }

    // Get API key from env
    const env = ctx.env as unknown as Record<string, unknown>;
    const apiKey = env.GOOGLE_PAGESPEED_API_KEY as string | undefined;

    if (!apiKey) {
      return adapterErr({
        code: "provider_unavailable",
        message: "PageSpeed API key is not configured.",
        retryable: false,
      });
    }

    // Fetch PSI (both strategies), CrUX History, and CrUX Record (per form factor) in parallel.
    // CrUX endpoints use a different quota (~150/min) from PSI (25K/day) — no conflict.
    // IMC-27: CrUX Record queries fetch LCP subparts + RTT + navigation types per form factor.
    //
    // Defensive: wrap CrUX calls in a try/catch so a synchronous throw (e.g. a
    // future bug, or an unexpected runtime error) can't fail the whole Promise.all.
    // These calls are SUPPLEMENTARY — the adapter must still return Lighthouse
    // data even if CrUX is entirely broken.
    const safeCruxHistory = fetchCruxHistory(targetUrl, apiKey, "url").catch(
      (err) => adapterErr({
        code: "network_error" as const,
        message: sanitizeErrorMessage(
          `CrUX History call threw unexpectedly: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: true,
      })
    );
    const safeCruxRecords = CRUX_FORM_FACTORS.map((ff) =>
      fetchCruxRecord(targetUrl, apiKey, ff).catch(
        (err) => adapterErr({
          code: "network_error" as const,
          message: sanitizeErrorMessage(
            `CrUX Record (${ff}) call threw unexpectedly: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable: true,
        })
      )
    );

    const [mobileResult, desktopResult, cruxHistoryResult, ...cruxRecordResults] =
      await Promise.all([
        fetchStrategy(targetUrl, "MOBILE", apiKey),
        fetchStrategy(targetUrl, "DESKTOP", apiKey),
        safeCruxHistory,
        ...safeCruxRecords,
      ]);

    if (!mobileResult.success) {
      return adapterErr(mobileResult.error);
    }
    if (!desktopResult.success) {
      return adapterErr(desktopResult.error);
    }

    const records: Record<string, unknown>[] = [];

    // Extract Lighthouse lab metrics
    records.push(extractLighthouseMetrics(mobileResult.data, "MOBILE"));
    records.push(extractLighthouseMetrics(desktopResult.data, "DESKTOP"));

    // Extract CrUX field data — URL-specific + origin-level (supplementary — may not exist)
    records.push(...extractCruxRecords(mobileResult.data, "MOBILE"));
    records.push(...extractCruxRecords(desktopResult.data, "DESKTOP"));

    // Append CrUX History (40-week trends) — non-blocking, failure doesn't fail the adapter
    if (cruxHistoryResult.success && cruxHistoryResult.data.length > 0) {
      records.push(...cruxHistoryResult.data);
    }

    // Append CrUX Record per form factor — non-blocking, failures are logged but don't
    // fail the adapter (many origins have no CrUX data). IMC-27 AC #1/#2/#5.
    // CrUXRecordData has an index signature so this assignment is type-safe.
    let cruxRecordFailureCount = 0;
    let cruxRecordEmptyCount = 0;
    for (const cruxRecordResult of cruxRecordResults) {
      if (!cruxRecordResult.success) {
        cruxRecordFailureCount += 1;
        continue;
      }
      if (cruxRecordResult.data === null) {
        cruxRecordEmptyCount += 1;
        continue;
      }
      records.push(cruxRecordResult.data);
    }

    // Observability parity with GSC/GA4/etc — emit structured completion log.
    console.log(
      JSON.stringify({
        event: "pagespeed_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        recordCount: records.length,
        cruxHistoryRecords: cruxHistoryResult.success ? cruxHistoryResult.data.length : 0,
        cruxHistoryFailed: !cruxHistoryResult.success,
        cruxRecordFailureCount,
        cruxRecordEmptyCount,
        timestamp: new Date().toISOString(),
      })
    );

    return adapterOk({
      platform: PLATFORM,
      accountId: targetUrl,
      dateRange: { start: new Date(), end: new Date() },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  async validateConnection(
    ctx: AdapterContext,
    _credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    // Validate via sync status — do NOT make HTTP request to user URL (SSRF risk)
    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No URL configured for PageSpeed validation.",
        retryable: false,
      });
    }

    // Connection is valid if an accountId (URL) is set
    return adapterOk(true);
  },

  async discoverAccounts(
    _ctx: AdapterContext,
    _credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // PageSpeed has no accounts to discover — the URL IS the account
    return adapterOk([]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return PAGESPEED_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return PAGESPEED_DIMENSIONS;
  },
};
